import type { InvestmentCommentaryResponse } from "@eu.mdotm.api/sphere";
import { Card } from "$root/components/EvolvedPrint/components/Card";
import type { PrintableProps } from "$root/components/EvolvedPrint/configuration/types";
import { type MarkdownRendererProps, MarkdownRenderer } from "$root/components/MarkdownRenderer/MarkdownRenderer";
import type { ReportTemplateItemMap } from "$root/functional-areas/reports/report-latest";
import { ForEach } from "@mdotm/utils/react-extra";
import { useMemo } from "react";
import ReactDOMServer from "react-dom/server";

export function getCommentaryProps(data: InvestmentCommentaryResponse): string[] {
	if (!data.commentary) {
		return [];
	}

	const description = `${data.commentary ?? ""}`;
	const marktdownToHtmlString = ReactDOMServer.renderToStaticMarkup(
		<MarkdownRenderer componentOverrides={markdownConfig}>{description}</MarkdownRenderer>,
	);

	const div = document.createElement("div");
	div.innerHTML = marktdownToHtmlString;
	const markdownContainer = div.children[0];
	const nodes: string[] = [];

	for (const x of markdownContainer.children) {
		nodes.push(x.outerHTML);
	}

	return nodes;
}

const Commentary = (
	props: PrintableProps<
		{
			config: ReportTemplateItemMap["commentary"];
			commentary: InvestmentCommentaryResponse;
		},
		string
	>,
): React.JSX.Element => {
	const {
		commentary,
		list,
		firstRender,
		config: { title, useTemplateNameAsTitle },
	} = props;
	const { status } = commentary;

	const cardTitle = useMemo(() => {
		if (!firstRender) {
			return undefined;
		}

		if (useTemplateNameAsTitle) {
			return commentary.template?.name ?? "";
		}
		return title ?? "";
	}, [commentary.template?.name, firstRender, title, useTemplateNameAsTitle]);

	if (list.length === 0 || status !== "COMPLETED") {
		return <></>;
	}

	return (
		<Card title={cardTitle} className="relative pb-6">
			<ForEach collection={list}>
				{(text, index) => (
					<>
						<div dangerouslySetInnerHTML={{ __html: text }} />

						{list.length - 1 === index && (
							<div className="absolute bottom-2">
								<p className="text-[#8c8ea8]">Content generated by Generative AI</p>
							</div>
						)}
					</>
				)}
			</ForEach>
		</Card>
	);
};

const markdownConfig: MarkdownRendererProps["componentOverrides"] = {
	h1: ({ node: _node, ...forward }) => (
		<h1 className="text-[14px] font-bold pb-2 pt-2" {...forward}>
			{forward.children}
		</h1>
	),
	h2: ({ node: _node, ...forward }) => (
		<h2 className="text-[12px] font-bold pb-2 pt-2" {...forward}>
			{forward.children}
		</h2>
	),
	h3: ({ node: _node, ...forward }) => (
		<h3 className="text-[10px] font-bold pb-2 pt-2" {...forward}>
			{forward.children}
		</h3>
	),
	ul: ({ node: _node, ...forward }) => <ul className="list-disc pl-4" {...forward} />,
	ol: ({ node: _node, ...forward }) => <ol className="list-decimal pl-4" {...forward} />,
	li: ({ node: _node, ...forward }) => <li {...forward} />,
	p: ({ node: _node, ...forward }) => <p className="text pb-2" {...forward} />,
	blockquote: ({ node: _node, ...forward }) => <blockquote className="bg-sky-100 px-2 py-1 rounded-sm" {...forward} />,
	table: ({ node: _node, ...props }) => <table className="w-full border-collapse" {...props} />,
	thead: ({ node: _node, ...props }) => <thead {...props} />,
	tr: ({ node: _node, ...props }) => <tr className={`border-b border-b-[color:var(--palette-N100)]`} {...props} />,
	td: ({ node: _node, ...props }) => <td className="text-left p-2 text-[10px]! first:w-2/4" {...props} />,
	th: ({ node: _node, ...props }) => (
		<th className="text-left px-2 py-1 font-bold! text-[10px]! uppercase! text-[#667085]" {...props} />
	),
	tbody: ({ node: _node, ...props }) => <tbody {...props} />,
};

export default Commentary;
